/** @format */

import clsx from 'clsx';
import {styled} from '@mui/material/styles';
import {Radio, Box} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import {RadioProps} from '@material-ui/core';

const PREFIX = 'GreenRadio';

const classes = {
  root: `${PREFIX}-root`,
  icon: `${PREFIX}-icon`,
  iconDark: `${PREFIX}-iconDark`,
  iconLight: `${PREFIX}-iconLight`,
  checkedIcon: `${PREFIX}-checkedIcon`,
  iconSquare: `${PREFIX}-iconSquare`,
};

const StyledRadio = styled(Radio)(({theme}) => ({
  padding: 0,
  [`& .${classes.icon}`]: {
    borderRadius: '50%',
    borderStyle: 'solid',
    borderWidth: 1,
    height: 24,
    paddingTop: 2,
    width: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  [`& .${classes.iconDark}`]: {
    backgroundColor: theme.palette.action.disabledBackground,
    borderColor: theme.palette.action.active,
  },

  [`& .${classes.iconLight}`]: {
    backgroundColor: theme.palette.secondary.main,
    borderColor: '#E0E0E0',
  },
  [`& .${classes.checkedIcon}`]: {
    backgroundColor: theme.palette.success.main,
    borderWidth: 0,
    '&:before': {
      backgroundColor: theme.palette.secondary.main,
      borderRadius: '50%',
      content: '""',
      display: 'block',
      height: 12,
      margin: 6,
      width: 12,
    },
  },
  [`& .${classes.iconSquare}`]: {
    width: theme.spacing(12),
    height: theme.spacing(12),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: theme.spacing(2),
    border: '1px solid #2B2B2B',
    background: 'transparent',
  },
}));

const StyledSquareRadio = styled(StyledRadio)(({theme}) => ({
  '&:hover': {
    backgroundColor: '#4D4D4D',
    borderRadius: theme.spacing(2),
  },
}));

type Props = {
  iconVariant?: 'circle' | 'check';
  variant?: 'DARK' | 'LIGHT' | 'SQUARE';
} & RadioProps;

const GreenRadio = ({iconVariant = 'circle', variant = 'DARK', ...rest}: Props) => {
  const iconStyle = (): string | undefined => {
    if (variant === 'DARK') {
      return clsx(classes.icon, classes.iconDark);
    }

    if (variant === 'SQUARE') {
      return clsx(classes.icon, classes.iconSquare);
    }

    if (variant === 'LIGHT') {
      return clsx(classes.icon, classes.iconLight);
    }

    return undefined;
  };

  const checkedIcon = () => {
    return variant === 'SQUARE' ? (
      <Box className={classes.iconSquare}>
        <CheckIcon fontSize='small' htmlColor='#FFF' />
      </Box>
    ) : iconVariant === 'circle' ? (
      <Box component='span' className={clsx(classes.icon, classes.checkedIcon)} />
    ) : iconVariant === 'check' ? (
      <Box component='span' className={iconStyle()} position='relative'>
        <img
          alt='Icon'
          height='12'
          src={new URL('./images/check.png', import.meta.url).href}
          width='12'
        />
      </Box>
    ) : null;
  };

  return variant === 'SQUARE' ? (
    <StyledSquareRadio
      checkedIcon={checkedIcon()}
      icon={<Box component='span' className={iconStyle()} />}
      {...rest}
    />
  ) : (
    <StyledRadio
      checkedIcon={checkedIcon()}
      icon={<Box component='span' className={iconStyle()} />}
      {...rest}
    />
  );
};

export default GreenRadio;
